import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { Room } from "@gooner/shared";
import { ApiHttpError, api } from "../lib/api";
import { useSession } from "../lib/session";
import { DisplayApp } from "./DisplayApp";
import { RoomFrame } from "./room/RoomFrame";

type Door = { kind: "loading" } | { kind: "open"; room: Room } | { kind: "closed" } | { kind: "removed" } | { kind: "dark" } | { kind: "error" };

/**
 * `/room/:id` — the address on the invite link.
 *
 * The page FETCHES the room and nothing more: link previewers fetch URLs (see
 * ClaimScreen), so nothing here may join, and joining is the room socket's, opened by
 * DisplayApp once the screen is up. No device id: a room viewer is an anonymous panel
 * like the pending screen, so its display hello names nobody and its goon session —
 * its own, started from the bar like anywhere — never lands on the host's.
 */
export function RoomScreen() {
  const { id } = useParams<{ id: string }>();
  const user = useSession((s) => s.user);
  const [door, setDoor] = useState<Door>({ kind: "loading" });

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setDoor({ kind: "loading" });
    api
      .room(id)
      .then((r) => !cancelled && setDoor({ kind: "open", room: r.room }))
      .catch((e: unknown) => {
        if (cancelled) return;
        const status = e instanceof ApiHttpError ? e.status : 0;
        setDoor(status === 404 ? { kind: "closed" } : status === 403 ? { kind: "removed" } : status === 503 ? { kind: "dark" } : { kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (!id || door.kind === "loading") return <Card>Opening the room…</Card>;
  if (door.kind === "closed") return <Card>That room has closed.</Card>;
  if (door.kind === "removed") return <Card>You were removed from this room.</Card>;
  if (door.kind === "dark") return <Card>Goon Rooms aren't open yet.</Card>;
  if (door.kind === "error") return <Card>That room couldn't be reached.</Card>;

  return (
    <div className="relative flex h-[100dvh] w-screen flex-col overflow-hidden bg-[var(--color-ink)]">
      {/* A ROOM IS THE HOST'S SCREEN: DisplayApp runs it exactly as /screen does (the host on
          their real view, a member following), and the room's frame — roster, chat, cards —
          wraps its glass, under the bar. */}
      <DisplayApp
        roomId={id}
        frame={(glass, room) => (
          <RoomFrame room={room} signedIn={!!user}>
            {glass}
          </RoomFrame>
        )}
      />
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-[100dvh] items-center justify-center bg-[var(--color-ink)] p-6 text-zinc-200">
      <div className="max-w-md space-y-3 rounded-2xl border border-[var(--color-edge)] bg-[var(--color-panel)] px-6 py-5 text-center text-sm">
        <p>{children}</p>
        <Link to="/rooms" className="inline-block text-[var(--color-accent-soft)] hover:underline">
          Back to Rooms
        </Link>
      </div>
    </div>
  );
}
